/* =========================================================================
   SWEATIX — theme.js  ·  motion engine + storefront interactions
   Works on Shopify (real /cart.js) and on the static preview (demo cart).
   Hard dependencies are optional & feature-detected: GSAP, ScrollTrigger,
   Lenis. Everything degrades gracefully + respects reduced-motion.
   ========================================================================= */
(function () {
  "use strict";
  const RM = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const HAS_GSAP = typeof window.gsap !== "undefined";
  const HAS_ST = HAS_GSAP && typeof window.ScrollTrigger !== "undefined";
  const IS_SHOPIFY = typeof window.Shopify !== "undefined";
  const $ = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => Array.from(c.querySelectorAll(s));
  const money = (cents) =>
    (cents / 100).toLocaleString(window.SWEATIX?.locale || "fr-FR", {
      style: "currency", currency: window.SWEATIX?.currency || "EUR",
      minimumFractionDigits: (cents % 100 === 0 ? 0 : 2),
    });
  const on = (el, ev, fn, o) => el && el.addEventListener(ev, fn, o);

  /* ------------------------------------------------------- Curtain loader */
  function curtain() {
    const c = $(".curtain");
    if (!c || RM) { c && c.remove(); return; }
    const bar = $(".curtain__bar", c);
    if (bar) bar.animate([{ width: "0%" }, { width: "100%" }], { duration: 700, fill: "forwards", easing: "cubic-bezier(.22,1,.36,1)" });
    const done = () => { c.classList.add("is-done"); setTimeout(() => c.remove(), 950); };
    if (document.readyState === "complete") setTimeout(done, 650);
    else window.addEventListener("load", () => setTimeout(done, 350));
    setTimeout(done, 2600); // safety
  }

  /* ------------------------------------------------------- Smooth scroll */
  let lenis = null;
  function smoothScroll() {
    // Smooth-scroll (Lenis) is OPT-IN. Native scroll by default = instant, zero lag.
    // Enable with: window.SWEATIX.smoothScroll = true (and a high lerp keeps it snappy).
    if (RM || typeof window.Lenis === "undefined" || !(window.SWEATIX && window.SWEATIX.smoothScroll === true)) return;
    lenis = new window.Lenis({ lerp: 0.2, wheelMultiplier: 1, smoothWheel: true });
    function raf(t) { lenis.raf(t); requestAnimationFrame(raf); }
    requestAnimationFrame(raf);
    if (HAS_ST) { lenis.on("scroll", window.ScrollTrigger.update); }
    window.SWEATIX_LENIS = lenis;
    // anchor links
    $$('a[href^="#"]').forEach((a) => {
      const id = a.getAttribute("href");
      if (id.length < 2) return;
      on(a, "click", (e) => { const t = $(id); if (t) { e.preventDefault(); lenis.scrollTo(t, { offset: -90 }); } });
    });
  }

  /* ------------------------------------------------------- Reveal on scroll */
  function reveals() {
    const items = $$("[data-reveal],[data-reveal-stagger]");
    if (!items.length) return;
    if (RM) { items.forEach((el) => el.classList.add("is-in")); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (!en.isIntersecting) return;
        const el = en.target;
        const delay = parseFloat(el.dataset.revealDelay || 0);
        if (el.hasAttribute("data-reveal-stagger")) {
          const step = parseFloat(el.dataset.revealStagger) || 0.08;
          $$(":scope > *", el).forEach((child, i) => { child.style.transitionDelay = (delay + i * step) + "s"; });
        } else if (delay) { el.style.transitionDelay = delay + "s"; }
        el.classList.add("is-in");
        io.unobserve(el);
      });
    }, { threshold: 0.12, rootMargin: "0px 0px -8% 0px" });
    items.forEach((el) => io.observe(el));
  }

  /* ------------------------------------------------------- Parallax */
  function parallax() {
    const els = $$("[data-parallax]");
    if (!els.length || RM) return;
    if (HAS_ST) {
      els.forEach((el) => {
        const speed = parseFloat(el.dataset.parallax) || 0.18;
        const img = $("img,video", el) || el;
        window.gsap.to(img, {
          yPercent: speed * 100, ease: "none",
          scrollTrigger: { trigger: el, start: "top bottom", end: "bottom top", scrub: true },
        });
      });
    } else {
      const upd = () => {
        const vh = window.innerHeight;
        els.forEach((el) => {
          const r = el.getBoundingClientRect();
          const speed = parseFloat(el.dataset.parallax) || 0.18;
          const prog = (r.top + r.height / 2 - vh / 2) / vh;
          const img = $("img,video", el) || el;
          img.style.transform = `translate3d(0,${(-prog * speed * 100).toFixed(2)}px,0)`;
        });
        requestAnimationFrame(upd);
      };
      requestAnimationFrame(upd);
    }
  }

  /* ------------------------------------------------------- Hero / kinetic GSAP */
  function heroMotion() {
    if (!HAS_GSAP || RM) return;
    const hero = $("[data-hero]");
    if (!hero) return;
    const tl = window.gsap.timeline({ defaults: { ease: "expo.out" } });
    const lines = $$("[data-hero-line]", hero);
    const fades = $$("[data-hero-fade]", hero);
    tl.from(lines, { yPercent: 110, duration: 1.1, stagger: 0.08 }, 0.15)
      .from(fades, { y: 24, opacity: 0, duration: 0.9, stagger: 0.1 }, "-=0.6");
    // Safety net: guarantee the final visible state even if the RAF-driven tween is throttled or interrupted.
    setTimeout(function () { try { window.gsap.set(lines.concat(fades), { clearProps: "transform,opacity" }); } catch (e) {} }, 2800);
    // NB: no hero-image parallax — a static object-fit:cover image can never reveal an edge/border.
  }

  /* ------------------------------------------------------- Counters */
  function counters() {
    const els = $$("[data-count]");
    if (!els.length) return;
    const run = (el) => {
      const target = parseFloat(el.dataset.count);
      const dec = (el.dataset.countDecimals | 0);
      const suffix = el.dataset.countSuffix || "";
      const prefix = el.dataset.countPrefix || "";
      if (RM) { el.textContent = prefix + target.toFixed(dec) + suffix; return; }
      const dur = 1500, t0 = performance.now();
      const tick = (t) => {
        const p = Math.min(1, (t - t0) / dur);
        const e = 1 - Math.pow(1 - p, 3);
        el.textContent = prefix + (target * e).toFixed(dec) + suffix;
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };
    const io = new IntersectionObserver((en) => en.forEach((e) => { if (e.isIntersecting) { run(e.target); io.unobserve(e.target); } }), { threshold: 0.6 });
    els.forEach((el) => io.observe(el));
  }

  /* ------------------------------------------------------- Marquee seamless */
  const _marqueeBase = new WeakMap();
  function marquees() {
    $$(".marquee").forEach((m) => {
      const track = $(".marquee__track", m);
      if (!track) return;
      let base = _marqueeBase.get(track);
      if (base === undefined) { base = track.innerHTML; _marqueeBase.set(track, base); }
      track.innerHTML = base;                                   // reset to the original content
      // 1) repeat until ONE copy is wider than the viewport (kills mid-loop gaps/holes)
      let guard = 0;
      while (track.scrollWidth < m.offsetWidth + 100 && guard < 24) { track.insertAdjacentHTML("beforeend", base); guard++; }
      // 2) duplicate the now viewport-wide content once → seamless translateX(-50%) loop
      track.insertAdjacentHTML("beforeend", track.innerHTML);
    });
  }
  let _mqT; on(window, "resize", () => { clearTimeout(_mqT); _mqT = setTimeout(marquees, 250); });

  /* ------------------------------------------------------- Magnetic buttons */
  function magnetic() {
    if (RM || window.matchMedia("(hover:none)").matches) return;
    $$("[data-magnetic]").forEach((el) => {
      const str = parseFloat(el.dataset.magnetic) || 0.32;
      on(el, "mousemove", (e) => {
        const r = el.getBoundingClientRect();
        const x = (e.clientX - r.left - r.width / 2) * str;
        const y = (e.clientY - r.top - r.height / 2) * str;
        el.style.transform = `translate(${x}px,${y}px)`;
      });
      on(el, "mouseleave", () => { el.style.transform = ""; });
    });
  }

  /* ------------------------------------------------------- Custom cursor */
  function cursor() {
    if (RM || window.matchMedia("(hover:none)").matches || window.innerWidth < 900) return;
    const dot = document.createElement("div"); dot.className = "cursor-dot";
    const ring = document.createElement("div"); ring.className = "cursor-ring";
    document.body.append(dot, ring);
    let rx = 0, ry = 0, x = 0, y = 0;
    on(window, "mousemove", (e) => { x = e.clientX; y = e.clientY; dot.style.transform = `translate(${x}px,${y}px) translate(-50%,-50%)`; });
    const loop = () => { rx += (x - rx) * 0.18; ry += (y - ry) * 0.18; ring.style.transform = `translate(${rx}px,${ry}px) translate(-50%,-50%)`; requestAnimationFrame(loop); };
    loop();
    $$("a,button,[data-magnetic],.opt,.pcard").forEach((el) => {
      on(el, "mouseenter", () => { ring.style.width = "62px"; ring.style.height = "62px"; ring.style.opacity = "1"; });
      on(el, "mouseleave", () => { ring.style.width = "40px"; ring.style.height = "40px"; ring.style.opacity = ".6"; });
    });
  }

  /* ------------------------------------------------------- Header */
  function header() {
    const h = $("[data-header]");
    if (!h) return;
    let last = window.scrollY, hidden = false;
    const upd = () => {
      const y = window.scrollY;
      h.classList.toggle("is-scrolled", y > 20);
      if (y > last && y > 320 && !hidden) { hidden = true; h.classList.add("is-hidden"); }
      else if (y < last && hidden) { hidden = false; h.classList.remove("is-hidden"); }
      last = y;
    };
    on(window, "scroll", upd, { passive: true }); upd();
    // mobile nav
    const burger = $("[data-nav-toggle]"), nav = $("[data-mobile-nav]");
    if (burger && nav) {
      // La classe va sur <body>, PAS sur <header> : le tiroir .mobile-nav est un FRERE
      // du header, donc `.nav-open .mobile-nav` ne matchait jamais — le burger se
      // transformait en croix mais rien ne s'ouvrait.
      const setNav = (open) => {
        document.body.classList.toggle("nav-open", open);
        document.body.classList.toggle("no-scroll", open);
        burger.setAttribute("aria-expanded", String(open));
        nav.setAttribute("aria-hidden", String(!open));
      };
      on(burger, "click", () => setNav(!document.body.classList.contains("nav-open")));
      $$("a", nav).forEach((a) => on(a, "click", () => setNav(false)));
      on(document, "keydown", (e) => { if (e.key === "Escape") setNav(false); });
      setNav(false);
    }
    // mega-menu (desktop hover handled by CSS :hover; keyboard support)
    $$("[data-has-mega]").forEach((li) => {
      const btn = $("a,button", li);
      on(btn, "focus", () => li.classList.add("mega-open"));
      on(li, "mouseleave", () => li.classList.remove("mega-open"));
      on(li, "mouseenter", () => li.classList.add("mega-open"));
    });
  }

  /* ------------------------------------------------------- Accordions */
  function accordions() {
    $$(".acc").forEach((acc) => {
      $$(".acc__item", acc).forEach((item) => {
        const btn = $(".acc__btn", item), panel = $(".acc__panel", item);
        if (!btn || !panel) return;
        on(btn, "click", () => {
          const open = item.classList.contains("is-open");
          if (acc.dataset.single !== "false") $$(".acc__item.is-open", acc).forEach((o) => { if (o !== item) { o.classList.remove("is-open"); $(".acc__panel", o).style.height = "0px"; $(".acc__btn", o).setAttribute("aria-expanded", "false"); } });
          item.classList.toggle("is-open", !open);
          btn.setAttribute("aria-expanded", String(!open));
          panel.style.height = open ? "0px" : panel.scrollHeight + "px";
        });
      });
    });
  }

  /* ------------------------------------------------------- Tabs */
  function tabs() {
    $$("[data-tabs]").forEach((wrap) => {
      const btns = $$("[data-tab]", wrap), panels = $$("[data-panel]", wrap);
      btns.forEach((b) => on(b, "click", () => {
        btns.forEach((x) => x.classList.remove("is-active"));
        panels.forEach((p) => p.classList.add("hide"));
        b.classList.add("is-active");
        const t = $(`[data-panel="${b.dataset.tab}"]`, wrap); if (t) t.classList.remove("hide");
      }));
    });
  }

  /* ------------------------------------------------------- Quantity steppers */
  function qtySteppers() {
    $$(".qty").forEach((q) => {
      const input = $("input", q), dec = $("[data-dec]", q), inc = $("[data-inc]", q);
      const min = parseInt(input?.min || "1", 10);
      const set = (v) => { input.value = Math.max(min, v); input.dispatchEvent(new Event("change", { bubbles: true })); };
      on(dec, "click", () => set(parseInt(input.value || min, 10) - 1));
      on(inc, "click", () => set(parseInt(input.value || min, 10) + 1));
    });
  }

  /* ------------------------------------------------------- Toasts */
  function toast(msg, opts = {}) {
    let wrap = $(".toast-wrap");
    if (!wrap) { wrap = document.createElement("div"); wrap.className = "toast-wrap"; document.body.appendChild(wrap); }
    const t = document.createElement("div"); t.className = "toast";
    t.innerHTML = `<span class="dot"></span><span>${msg}</span>`;
    wrap.appendChild(t);
    requestAnimationFrame(() => t.classList.add("is-in"));
    setTimeout(() => { t.classList.remove("is-in"); setTimeout(() => t.remove(), 400); }, opts.duration || 2600);
  }
  window.sweatixToast = toast;

  /* =====================================================================
     CART  (Shopify /cart.js  ·  demo fallback on static preview)
     ===================================================================== */
  const DEMO = !IS_SHOPIFY;
  const demoState = { items: [], item_count: 0, total_price: 0 };

  async function cartGet() {
    if (DEMO) return { ...demoState };
    const r = await fetch("/cart.js", { headers: { Accept: "application/json" } });
    return r.json();
  }
  async function cartAdd(payload) {
    if (DEMO) {
      const price = payload.price || 3490;
      const existing = demoState.items.find((i) => i.id === payload.id);
      if (existing) existing.quantity += payload.quantity || 1;
      else demoState.items.push({
        id: payload.id, quantity: payload.quantity || 1, title: payload.title || "Maillot Sweatix",
        variant_title: payload.variant_title || "", price, line_price: price * (payload.quantity || 1),
        image: payload.image || null, url: payload.url || "#", product_title: payload.title || "Maillot Sweatix",
        options_with_values: payload.options || [],
      });
      recalcDemo(); return { ...demoState };
    }
    const r = await fetch("/cart/add.js", { method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify({ items: [payload] }) });
    if (!r.ok) throw await r.json();
    return r.json();
  }
  async function cartChange(key, quantity) {
    if (DEMO) {
      const it = demoState.items.find((i) => String(i.id) === String(key));
      if (it) { it.quantity = quantity; if (quantity <= 0) demoState.items = demoState.items.filter((i) => i !== it); else it.line_price = it.price * quantity; }
      recalcDemo(); return { ...demoState };
    }
    const r = await fetch("/cart/change.js", { method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify({ id: key, quantity }) });
    return r.json();
  }
  function recalcDemo() {
    demoState.item_count = demoState.items.reduce((a, i) => a + i.quantity, 0);
    demoState.total_price = demoState.items.reduce((a, i) => a + i.price * i.quantity, 0);
  }

  const FREE_SHIP = window.SWEATIX?.freeShipThreshold ?? 6000; // cents
  const FLOCAGE_VARIANT = window.SWEATIX?.flocageVariantId ?? null;

  function renderDrawer(cart) {
    const drawer = $("[data-cart-drawer]");
    if (!drawer) return;
    const body = $("[data-cart-body]", drawer);
    const foot = $("[data-cart-foot]", drawer);
    const empty = $("[data-cart-empty]", drawer);
    updateCount(cart.item_count);
    if (!cart.items || !cart.items.length) {
      if (empty) empty.classList.remove("hide");
      if (body) body.innerHTML = "";
      if (foot) foot.classList.add("hide");
      return;
    }
    if (empty) empty.classList.add("hide");
    if (foot) foot.classList.remove("hide");
    if (body) body.innerHTML = cart.items.map(lineRow).join("");
    // free-ship progress
    const left = Math.max(0, FREE_SHIP - cart.total_price);
    const pct = Math.min(100, (cart.total_price / FREE_SHIP) * 100);
    const bar = $("[data-ship-bar]", drawer), msg = $("[data-ship-msg]", drawer);
    if (bar) bar.style.width = pct + "%";
    if (msg) msg.innerHTML = left > 0
      ? `Plus que <strong>${money(left)}</strong> pour la <strong>livraison offerte</strong> 🚚`
      : `<strong>Livraison offerte débloquée</strong> ✓`;
    const total = $("[data-cart-total]", drawer); if (total) total.textContent = money(cart.total_price);
    bindLineEvents(drawer);
  }
  function estFlocage(it) {
    return FLOCAGE_VARIANT && Number(it.variant_id) === Number(FLOCAGE_VARIANT);
  }
  function lineRow(it) {
    const key = it.key || it.id;
    // La ligne de flocage est rattachee a un maillot : pas de quantite ni de
    // suppression propres, sinon le client peut la desynchroniser de son maillot.
    if (estFlocage(it)) {
      const pour = (it.properties && it.properties["Pour"]) || "";
      const nom = (it.properties && it.properties["Nom"]) || "";
      const num = (it.properties && it.properties["Numéro"]) || "";
      const detail = [nom, num].filter(Boolean).join(" · ");
      return `<div class="cart-line cart-line--option" data-line="${key}">
        <div class="cart-line__info">
          <div class="between">
            <span class="cart-line__title">Flocage${detail ? " — " + detail : ""}</span>
            <div class="cart-line__price">${money(it.line_price)}</div>
          </div>
          ${pour ? `<div class="cart-line__opts">sur ${pour}</div>` : ""}
        </div></div>`;
    }
    const img = it.image ? `<img src="${it.image}" alt="" loading="lazy">` : `<div class="ph"></div>`;
    const opts = it.variant_title && it.variant_title !== "Default Title" ? it.variant_title : (it.options_with_values || []).map((o) => o.value).join(" · ");
    // Les proprietes commencant par « _ » sont internes : on ne les montre pas.
    const props = Object.entries(it.properties || {})
      .filter(([k, v]) => k.charAt(0) !== "_" && v)
      .map(([k, v]) => `<li><span class="cart-line__prop-k">${k}</span> ${v}</li>`)
      .join("");
    return `<div class="cart-line" data-line="${key}">
      <a class="cart-line__media" href="${it.url || "#"}">${img}</a>
      <div class="cart-line__info">
        <div class="between"><a href="${it.url || "#"}" class="cart-line__title">${it.product_title || it.title}</a>
          <button class="cart-line__rm" data-line-remove="${key}" aria-label="Retirer">&times;</button></div>
        ${opts ? `<div class="cart-line__opts">${opts}</div>` : ""}
        ${props ? `<ul class="cart-line__props">${props}</ul>` : ""}
        <div class="between" style="margin-top:.5rem">
          <div class="qty qty--sm"><button data-line-dec="${key}" aria-label="-">&minus;</button>
            <input value="${it.quantity}" inputmode="numeric" data-line-qty="${key}" aria-label="Quantité">
            <button data-line-inc="${key}" aria-label="+">+</button></div>
          <div class="cart-line__price">${money(it.line_price)}</div>
        </div>
      </div></div>`;
  }
  function bindLineEvents(drawer) {
    const refresh = async (key, q) => {
      try {
        setBusy(drawer, true);
        const avantMaj = await cartGet();
        const ligne = (avantMaj.items || []).find((i) => String(i.key || i.id) === String(key));
        const ref = ligne && ligne.properties && ligne.properties._ref;
        let c = await cartChange(key, q);
        // Le flocage suit le maillot auquel il est rattache : meme quantite,
        // et il disparait avec lui.
        if (ref && ligne && !estFlocage(ligne)) {
          const liee = (c.items || []).find((i) => i.properties && i.properties._ref === ref && estFlocage(i));
          if (liee) c = await cartChange(liee.key || liee.id, q);
        }
        renderDrawer(c);
      } finally { setBusy(drawer, false); }
    };
    $$("[data-line-inc]", drawer).forEach((b) => on(b, "click", () => { const k = b.dataset.lineInc; const i = $(`[data-line-qty="${k}"]`, drawer); refresh(k, parseInt(i.value, 10) + 1); }));
    $$("[data-line-dec]", drawer).forEach((b) => on(b, "click", () => { const k = b.dataset.lineDec; const i = $(`[data-line-qty="${k}"]`, drawer); refresh(k, parseInt(i.value, 10) - 1); }));
    $$("[data-line-remove]", drawer).forEach((b) => on(b, "click", () => refresh(b.dataset.lineRemove, 0)));
    $$("[data-line-qty]", drawer).forEach((i) => on(i, "change", () => refresh(i.dataset.lineQty, Math.max(0, parseInt(i.value, 10) || 0))));
  }
  function setBusy(el, b) { el.classList.toggle("is-loading", b); }

  function updateCount(n) {
    $$("[data-cart-count]").forEach((el) => {
      el.textContent = n;
      el.classList.toggle("is-empty", !n);
      if (n && !RM) { el.animate([{ transform: "scale(1)" }, { transform: "scale(1.5)" }, { transform: "scale(1)" }], { duration: 360, easing: "ease-out" }); }
    });
  }

  function openDrawer() {
    const d = $("[data-cart-drawer]"); if (!d) return;
    d.classList.add("is-open"); document.body.classList.add("no-scroll");
    $("[data-cart-overlay]")?.classList.add("is-open");
  }
  function closeDrawer() {
    const d = $("[data-cart-drawer]"); if (!d) return;
    d.classList.remove("is-open"); document.body.classList.remove("no-scroll");
    $("[data-cart-overlay]")?.classList.remove("is-open");
  }

  function cartUI() {
    $$("[data-cart-open]").forEach((b) => on(b, "click", (e) => { e.preventDefault(); openDrawer(); }));
    $$("[data-cart-close],[data-cart-overlay]").forEach((b) => on(b, "click", closeDrawer));
    on(document, "keydown", (e) => { if (e.key === "Escape") closeDrawer(); });
    // add-to-cart forms (product + quick add)
    $$("form[data-product-form],[data-quick-add]").forEach((el) => {
      const handler = async (e) => {
        e.preventDefault();
        const btn = $('[type="submit"],[data-add]', el) || el;
        const id = el.dataset.variantId || $('[name="id"]', el)?.value;
        if (!id) { toast("Sélectionne une taille"); return; }
        const qty = parseInt($('[name="quantity"]', el)?.value || "1", 10);
        const props = {};
        $$("[data-prop]", el).forEach((p) => { if (p.value.trim()) props[p.dataset.prop] = p.value.trim(); });

        btn.classList.add("is-busy"); const label = btn.querySelector("[data-add-label]");
        const prev = label ? label.textContent : btn.textContent;
        try {
          await cartAdd({ id: Number(id) || id, quantity: qty, properties: Object.keys(props).length ? props : undefined,
            price: parseInt(el.dataset.price || "0", 10) || undefined, title: el.dataset.title, variant_title: el.dataset.variantTitle,
            image: el.dataset.image, url: el.dataset.url, options: el.dataset.options ? JSON.parse(el.dataset.options) : undefined });
          const c = await cartGet(); renderDrawer(c); openDrawer();
          if (label) label.textContent = "Ajouté ✓"; toast("Ajouté au panier");
          setTimeout(() => { if (label) label.textContent = prev; }, 1600);
        } catch (err) {
          toast((err && (err.description || err.message)) || "Rupture de stock"); console.warn(err);
        } finally { btn.classList.remove("is-busy"); }
      };
      if (el.tagName === "FORM") on(el, "submit", handler);
      else on($("[data-add]", el) || el, "click", handler);
    });
    cartGet().then((c) => renderDrawer(c)).catch(() => {});
  }

  /* ------------------------------------------------------- Product page UI */
  function productUI() {
    const root = $("[data-product]");
    if (!root) return;
    const variants = (() => { try { return JSON.parse($("[data-variants-json]", root)?.textContent || "[]"); } catch { return []; } })();
    const form = $("form[data-product-form]", root);
    const selected = {};
    const compute = () => {
      const v = variants.find((vr) => (vr.options || []).every((o, i) => selected["option" + (i + 1)] === o)) || null;
      const addBtn = $("[data-add]", root); const idInput = $('[name="id"]', form);
      const priceEl = $("[data-product-price]", root);
      if (v) {
        if (idInput) idInput.value = v.id; if (form) form.dataset.variantId = v.id;
        if (priceEl && v.price != null) priceEl.textContent = money(v.price);
        if (addBtn) {
          addBtn.disabled = !v.available;
          $("[data-add-label]", addBtn) && ($("[data-add-label]", addBtn).textContent = v.available ? "Ajouter au panier" : "Rupture de stock");
        }
        // update main gallery if variant has featured image index
        if (v.featured_media != null) { const g = $(`[data-media-id="${v.featured_media}"]`, root); g && g.scrollIntoView({ block: "nearest", inline: "center" }); }
      } else if (addBtn) { $("[data-add-label]", addBtn) && ($("[data-add-label]", addBtn).textContent = "Choisis tes options"); addBtn.disabled = true; }
    };
    $$("[data-option-index]", root).forEach((group) => {
      const idx = group.dataset.optionIndex;
      $$(".opt", group).forEach((opt) => on(opt, "click", () => {
        $$(".opt", group).forEach((o) => o.classList.remove("is-active"));
        opt.classList.add("is-active"); selected["option" + idx] = opt.dataset.value;
        compute();
      }));
      // preselect first available
      const first = $(".opt:not([disabled])", group); if (first) { first.classList.add("is-active"); selected["option" + idx] = first.dataset.value; }
    });
    compute();

    // Le flocage n'est pas une ligne de panier separee : c'est une OPTION de
    // variante. Des que le client saisit un nom ou un numero, on bascule sur la
    // variante « Avec flocage » — donc le prix du maillot lui-meme passe a +5 €.
    const groupeFloc = $("[data-option-flocage]", root);
    if (groupeFloc) {
      const idxFloc = groupeFloc.dataset.optionIndex;
      const champs = $$("[data-prop]", root);
      const valeurs = $$(".opt", groupeFloc).map((o) => o.dataset.value);
      const valAvec = valeurs.find((v) => /avec/i.test(v));
      const valSans = valeurs.find((v) => /sans/i.test(v));
      const majFlocage = () => {
        const rempli = champs.some((c) => c.value.trim() !== "");
        const cible = rempli ? valAvec : valSans;
        if (!cible) return;
        selected["option" + idxFloc] = cible;
        $$(".opt", groupeFloc).forEach((o) => o.classList.toggle("is-active", o.dataset.value === cible));
        compute();
      };
      champs.forEach((c) => on(c, "input", majFlocage));
      majFlocage();
    }

    // gallery thumbs
    const main = $("[data-gallery-main]", root);
    $$("[data-thumb]", root).forEach((t) => on(t, "click", () => {
      $$("[data-thumb]", root).forEach((x) => x.classList.remove("is-active")); t.classList.add("is-active");
      if (!main) return;
      const img = $("img", main);
      const src = t.dataset.full || $("img", t)?.src;
      if (!img || !src) return;
      img.style.opacity = "0";
      setTimeout(() => {
        // Tant qu'un srcset est present, le navigateur IGNORE le changement de src :
        // c'est ce qui figeait la galerie. On remplace les deux, ou on vide le srcset.
        if (t.dataset.srcset) { img.srcset = t.dataset.srcset; } else { img.removeAttribute("srcset"); }
        img.src = src;
        img.style.opacity = "1";
      }, 150);
    }));
  }

  /* ------------------------------------------------------- Collection filters */
  function collectionUI() {
    const root = $("[data-collection]");
    if (!root) return;
    const grid = $("[data-product-grid]", root);
    const cards = $$("[data-pcard]", grid || root);
    // client-side filter chips (works on demo + as progressive enhancement)
    $$("[data-filter]", root).forEach((chip) => on(chip, "click", () => {
      const key = chip.dataset.filter, val = chip.dataset.value;
      const group = chip.closest("[data-filter-group]");
      if (group && group.dataset.multi !== "true") $$("[data-filter]", group).forEach((c) => c.classList.remove("is-active"));
      chip.classList.toggle("is-active");
      applyFilters(root, cards);
    }));
    const sort = $("[data-sort]", root);
    on(sort, "change", () => {
      const v = sort.value; const arr = cards.slice();
      arr.sort((a, b) => {
        const pa = parseFloat(a.dataset.price || 0), pb = parseFloat(b.dataset.price || 0);
        if (v === "price-asc") return pa - pb; if (v === "price-desc") return pb - pa;
        return (a.dataset.order | 0) - (b.dataset.order | 0);
      });
      arr.forEach((c) => grid.appendChild(c));
    });
    // mobile filter drawer
    const fOpen = $("[data-filter-open]", root), fPanel = $("[data-filter-panel]", root);
    on(fOpen, "click", () => fPanel?.classList.toggle("is-open"));
    $("[data-filter-close]", root) && on($("[data-filter-close]", root), "click", () => fPanel?.classList.remove("is-open"));
  }
  function applyFilters(root, cards) {
    const active = {};
    $$("[data-filter].is-active", root).forEach((c) => { (active[c.dataset.filter] ||= []).push(c.dataset.value); });
    let shown = 0;
    cards.forEach((card) => {
      const ok = Object.entries(active).every(([k, vals]) => { const cv = (card.dataset[k] || "").split(",").map((s) => s.trim()); return vals.some((v) => cv.includes(v)); });
      card.classList.toggle("hide", !ok); if (ok) shown++;
    });
    const count = $("[data-result-count]", root); if (count) count.textContent = shown;
    const none = $("[data-no-results]", root); if (none) none.classList.toggle("hide", shown > 0);
  }

  /* ------------------------------------------------------- Predictive search */
  function searchUI() {
    const trg = $$("[data-search-open]"), modal = $("[data-search-modal]");
    if (!modal) return;
    const input = $("[data-search-input]", modal), results = $("[data-search-results]", modal);
    const open = () => { modal.classList.add("is-open"); document.body.classList.add("no-scroll"); setTimeout(() => input?.focus(), 80); };
    const close = () => { modal.classList.remove("is-open"); document.body.classList.remove("no-scroll"); };
    trg.forEach((t) => on(t, "click", (e) => { e.preventDefault(); open(); }));
    $$("[data-search-close],[data-search-overlay]", modal).forEach((b) => on(b, "click", close));
    on(document, "keydown", (e) => { if (e.key === "Escape") close(); if ((e.metaKey || e.ctrlKey) && e.key === "k") { e.preventDefault(); open(); } });
    if (!input || DEMO) return;
    let timer;
    on(input, "input", () => {
      clearTimeout(timer); const q = input.value.trim();
      if (q.length < 2) { results.innerHTML = ""; return; }
      timer = setTimeout(async () => {
        try {
          const r = await fetch(`/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6`);
          const data = await r.json();
          const prods = data.resources?.results?.products || [];
          results.innerHTML = prods.map((p) => `<a class="s-result" href="${p.url}"><img src="${p.featured_image?.url || ""}" alt="" loading="lazy"><span><strong>${p.title}</strong><em>${p.price}</em></span></a>`).join("") || `<p class="text-muted">Aucun résultat pour « ${q} »</p>`;
        } catch (e) { /* ignore */ }
      }, 220);
    });
  }

  /* ------------------------------------------------------- Newsletter / contact (demo) */
  function formsDemo() {
    if (!DEMO) return;
    $$("form[data-demo-form]").forEach((f) => on(f, "submit", (e) => {
      e.preventDefault();
      const ok = $("[data-form-success]", f);
      if (ok) { ok.classList.remove("hide"); f.querySelectorAll("input,textarea,button").forEach((i) => i.setAttribute("disabled", "")); }
      else toast("Merci ! (démo — branché à Shopify en prod)");
    }));
  }

  /* ------------------------------------------------------- Order tracking (demo UI) */
  function trackingUI() {
    const root = $("[data-tracking]");
    if (!root) return;
    const form = $("form", root), steps = $("[data-track-steps]", root);
    on(form, "submit", (e) => {
      e.preventDefault();
      steps?.classList.remove("hide");
      if (!RM && HAS_GSAP) window.gsap.from("[data-track-steps] .track-step", { x: -20, opacity: 0, stagger: 0.12, duration: 0.6, ease: "power2.out" });
      steps?.scrollIntoView({ behavior: RM ? "auto" : "smooth", block: "center" });
    });
  }

  /* ------------------------------------------------------- Favorites (localStorage) */
  function favorites() {
    let favs;
    try { favs = JSON.parse(localStorage.getItem("sweatix:favs") || "[]"); } catch { favs = []; }
    const sync = () => { $$("[data-fav]").forEach((b) => b.classList.toggle("is-fav", favs.includes(b.dataset.fav))); $$("[data-fav-count]").forEach((e) => e.textContent = favs.length); };
    $$("[data-fav]").forEach((b) => on(b, "click", (e) => { e.preventDefault(); const id = b.dataset.fav; const i = favs.indexOf(id); if (i > -1) favs.splice(i, 1); else { favs.push(id); toast("Ajouté aux favoris"); } localStorage.setItem("sweatix:favs", JSON.stringify(favs)); sync(); }));
    sync();
  }

  /* ------------------------------------------------------- Split-text heading reveal */
  const EASE = "cubic-bezier(.22,1,.36,1)";
  function splitWords(el) {
    const wrap = (text) => {
      const frag = document.createDocumentFragment();
      text.split(/(\s+)/).forEach((part) => {
        if (!part.trim()) { frag.appendChild(document.createTextNode(part)); return; }
        const w = document.createElement("span"); w.className = "rw";
        const i = document.createElement("span"); i.className = "rw-i"; i.textContent = part;
        w.appendChild(i); frag.appendChild(w);
      });
      return frag;
    };
    const walk = (node) => Array.from(node.childNodes).forEach((c) => {
      if (c.nodeType === 3 && c.textContent.trim()) node.replaceChild(wrap(c.textContent), c);
      else if (c.nodeType === 1 && !c.classList.contains("rw")) walk(c);
    });
    walk(el);
  }
  function splitReveal() {
    const items = [];
    $$("[data-split], .sec-head__title, .footer__cta-title, .news__title").forEach((el) => {
      if (el.dataset.rwDone) return; el.dataset.rwDone = "1";
      splitWords(el);
      const inners = $$(".rw-i", el);
      if (!inners.length || RM) return;
      inners.forEach((n) => { n.style.transform = "translateY(110%)"; n.style.opacity = "0"; });
      items.push({ el, inners, done: false });
    });
    if (!items.length) return;
    // Scroll-based (rect check) → RAF-independent, fires on real scroll, never leaves text hidden.
    const check = () => {
      const vh = window.innerHeight;
      items.forEach((it) => {
        if (it.done || it.el.getBoundingClientRect().top > vh * 0.9) return;
        it.done = true;
        it.inners.forEach((n, i) => { n.style.transition = `transform .9s ${EASE} ${i * 0.05}s, opacity .6s ${i * 0.05}s`; n.style.transform = "none"; n.style.opacity = "1"; });
      });
    };
    on(window, "scroll", check, { passive: true }); on(window, "resize", check); check();
  }

  /* ------------------------------------------------------- Scroll progress bar */
  function scrollProgress() {
    if (RM) return;
    const bar = document.createElement("div"); bar.className = "scroll-prog"; bar.setAttribute("aria-hidden", "true");
    document.body.appendChild(bar);
    const upd = () => { const h = document.documentElement; const max = h.scrollHeight - h.clientHeight; bar.style.transform = "scaleX(" + (max > 0 ? h.scrollTop / max : 0) + ")"; };
    on(window, "scroll", upd, { passive: true }); on(window, "resize", upd); upd();
  }

  /* ------------------------------------------------------- Marquee velocity skew */
  function velocityFX() {
    if (RM) return;
    const ms = $$(".marquee");
    if (!ms.length) return;
    let last = window.scrollY, vel = 0;
    on(window, "scroll", () => { vel += (window.scrollY - last); last = window.scrollY; }, { passive: true });
    const loop = () => { vel *= 0.86; const sk = Math.max(-7, Math.min(7, vel * 0.28));
      ms.forEach((m) => { m.style.transform = Math.abs(sk) < 0.05 ? "" : "skewX(" + sk.toFixed(2) + "deg)"; });
      requestAnimationFrame(loop); };
    loop();
  }

  /* ------------------------------------------------------- 3D tilt on cards */
  function tilt() {
    if (RM || window.matchMedia("(hover:none)").matches) return;
    $$("[data-tilt], .cat__card, .catsplit__card").forEach((el) => {
      const max = parseFloat(el.dataset.tilt) || 6;
      on(el, "mousemove", (e) => {
        const r = el.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width - 0.5, py = (e.clientY - r.top) / r.height - 0.5;
        el.style.transform = `perspective(900px) rotateY(${px * max}deg) rotateX(${-py * max}deg)`;
      });
      on(el, "mouseleave", () => { el.style.transform = ""; });
    });
  }

  /* ------------------------------------------------------- Boot */
  function init() {
    productVideos();
    curtain(); smoothScroll(); marquees(); reveals(); parallax(); heroMotion();
    splitReveal(); scrollProgress(); velocityFX(); tilt();
    counters(); magnetic(); cursor(); header(); accordions(); tabs(); qtySteppers();
    cartUI(); productUI(); collectionUI(); searchUI(); formsDemo(); trackingUI(); favorites();
    if (HAS_ST) setTimeout(() => window.ScrollTrigger.refresh(), 400);
    document.documentElement.classList.add("js-ready");
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();


  /* ---------------------------------------------------- Vidéos produit */
  /* Les cartes portant une vidéo ne la jouent que lorsqu'elles sont visibles :
     pas de lecture en arrière-plan, pas de bande passante gaspillée. */
  function productVideos() {
    const clips = $$("video[data-pvid]");
    if (!clips.length || !("IntersectionObserver" in window)) return;
    const doux = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (doux) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        const v = e.target;
        if (e.isIntersecting) {
          if (v.preload === "none") v.preload = "auto";
          const p = v.play();
          if (p && p.catch) p.catch(() => {});
        } else if (!v.paused) {
          v.pause();
        }
      });
    }, { threshold: 0.35 });
    clips.forEach((v) => io.observe(v));
  }

  // Expose minimal API (Shopify section rerender hooks, theme editor)
  window.SWEATIX_THEME = { reveals, marquees, accordions, productUI, collectionUI, cartUI, toast, openDrawer, closeDrawer, renderDrawer, cartGet };
  document.addEventListener("shopify:section:load", init);
})();
